<template>
  <PageSection :background-class="$style.csrContact">
    <PageContainer>
      <PageHeading tag-name="h1">
        {{ $t('csr.section.contact.title') }}
      </PageHeading>
    </PageContainer>

    <ContactForm
      :class="$style.contactForm"
      :value="model"
      :submit-handler="submitHandler"
      :form="formInputs"
    />
  </PageSection>
</template>

<script lang="ts">
import type { Client } from '@viddsee/server-api-client';
import type { I18n } from '@/scripts/libs/page-loader/translation-support';
// From partner page
import { FormInputProps } from '@/scripts/pages/partners/sections/ContactUsSection/FormInput.types';
import ContactForm from '@/scripts/pages/partners/sections/ContactUsSection/ContactForm.ts.vue';
// CSR
import PageHeading from '@/scripts/pages/onepager/components/PageHeading.ts.vue';
import PageSection from '@/scripts/pages/onepager/components/PageSection.ts.vue';
import PageContainer from '@/scripts/pages/onepager/components/PageContainer.ts.vue';
import { CsrBaseComponent } from '../components/CsrBaseComponent';

const createContactFormInputs = (i18nInstance: { $t: I18n['$t'] }): FormInputProps[] => {
  const localizePlacholder = ((): (() => string) => {
    let index = 0;
    return (): string => `csr.section.contact.form.items.${index++}.placeholder`;
  })();

  return [
    {
      type: 'text',
      name: 'Name',
      placeholder: i18nInstance.$t(localizePlacholder()),
      required: true,
    },
    {
      type: 'text',
      name: 'Country',
      placeholder: i18nInstance.$t(localizePlacholder()),
      required: true,
    },
    {
      type: 'text',
      name: 'Email',
      placeholder: i18nInstance.$t(localizePlacholder()),
      required: true,
    },
    {
      type: 'text',
      name: 'Phone Number',
      placeholder: i18nInstance.$t(localizePlacholder()),
    },
    {
      type: 'text',
      name: 'Company Name',
      placeholder: i18nInstance.$t(localizePlacholder()),
    },
    {
      type: 'text',
      name: 'Notes',
      placeholder: i18nInstance.$t(localizePlacholder()),
    },
  ];
};

interface DataModel {
  'Name': string;
  'Country': string;
  'Email': string;
  'Phone Number': string;
  'Company Name': string;
  'Notes': string;
}

export default CsrBaseComponent.extend({
  components: {
    PageHeading,
    PageSection,
    PageContainer,
    ContactForm,
  },
  data() {
    const formInputs = createContactFormInputs(this);
    const model = Object.fromEntries(formInputs.map(({ name }) => [name, ''])) as unknown as DataModel;

    return {
      formInputs,
      model,
      formsApiImporter: null as null | Promise<Client.FormsApi>,
    };
  },

  mounted() {
    this.getFormsApiModule();
  },

  methods: {
    getFormsApiModule(): Promise<Client.FormsApi> {
      if (this.formsApiImporter) return this.formsApiImporter;

      this.formsApiImporter = import('@/scripts/webcall/v3/forms').then(({ formsApi }) => formsApi);
      return this.formsApiImporter;
    },

    async submitHandler(model: DataModel, token: string): Promise<void> {
      this.model = model;

      const formsApi = await this.getFormsApiModule();
      await formsApi.post({ formName: 'viddsee_for_good' }, undefined, {
        answers: {
          ...model,
          captchaToken: token,
        },
      });
    },
  },
});
</script>

<style lang="scss" module>
@import '~@/scripts/pages/onepager/styles/variables-entry';
@import '../styles/variables';

.csrContact {
  $section-primary-color: white;
  $dark-viddsee-pink: $page-primary-color;

  background-color: $dark-viddsee-pink;
  color: $section-primary-color;

  :global {
    textarea,
    input {
      // Standard layout
      display: block;
      width: 100%;
      max-width: 360px;
      margin-left: auto;
      margin-right: auto;

      // Default styling removal
      background: none;
      border: none;

      // Additional styling
      &::placeholder { color: rgba($section-primary-color, 0.8); }
      border: 0.5px solid $section-primary-color;
      padding: $spacer $spacer--x2;

      margin-bottom: $spacer--x2;
    }

    button {
      width: 210px;
    }
  }
}
</style>
